/**
 * Utility функции для обработки ошибок API
 * КРИТИЧНО: Используйте эти функции для ВСЕХ ошибок API
 */

import axios, { type AxiosError } from 'axios';
import type { ApiErrorResponse, ParsedApiError } from '../types/api.types';

/**
 * Безопасно парсит ошибку (unknown) в ParsedApiError.
 * Используйте в catch (error: unknown) вместо parseApiError(error as any).
 */
export function safeParseApiError(error: unknown): ParsedApiError {
  if (axios.isAxiosError(error)) {
    return parseApiError(error);
  }
  return {
    message: error instanceof Error ? error.message : String(error ?? 'Unknown error'),
    statusCode: 0,
  };
}

/**
 * Парсит ошибку от API в единообразный формат
 * КРИТИЧНО: Используйте эту функцию для ВСЕХ ошибок API
 *
 * @param error - Ошибка от axios
 * @returns Распарсенная ошибка с сообщением и деталями
 *
 * @example
 * ```ts
 * try {
 *   await api.createEvent(data);
 * } catch (error) {
 *   const apiError = parseApiError(error as AxiosError);
 *   toast.error(apiError.message);
 * }
 * ```
 */
/**
 * Канонический источник сообщения по ADR-002 — вложенный `error.message`
 * (`shared/filters/global-exception.filter.ts` на бэкенде). Верхний `message`
 * тот же фильтр отдаёт лишь как legacy-дубль: «сохраняются до миграции фронта
 * (W2-S2), затем подлежат удалению». Живая проба прода 2026-08-26 подтвердила,
 * что сегодня приходят ОБА поля, — поэтому вложенный источник читается ПОСЛЕ
 * плоского и сегодняшнего поведения не меняет. В день удаления дубля без него
 * каждая ошибка здесь превратилась бы в «An error occurred».
 *
 * Тип `ApiErrorResponse.error` объявлен строкой и с этим конвертом расходится —
 * сужаем локально, не переписывая контракт всех его потребителей.
 */
function nestedEnvelopeMessage(response: unknown): string | undefined {
  if (typeof response !== 'object' || response === null) return undefined;
  const nested = (response as { error?: unknown }).error;
  if (typeof nested !== 'object' || nested === null) return undefined;
  const message = (nested as { message?: unknown }).message;
  return typeof message === 'string' && message.trim() ? message : undefined;
}

export function parseApiError(error: AxiosError): ParsedApiError {
  const response = error.response?.data as ApiErrorResponse | undefined;

  if (response) {
    return {
      message:
        response.message || nestedEnvelopeMessage(response) || 'An error occurred',
      details: response.details ?? [],
      statusCode: response.statusCode || error.response?.status || 500,
    };
  }

  // Network error или другая ошибка
  return {
    message: error.message || 'Network error',
    statusCode: error.response?.status || 0,
  };
}

/**
 * Получает ошибку для конкретного поля из validation errors
 * Полезно для react-hook-form, Formik, и других form libraries
 *
 * @param error - Ошибка от axios
 * @param fieldName - Имя поля
 * @returns Сообщение об ошибке для поля или undefined
 *
 * @example
 * ```ts
 * try {
 *   await api.createEvent(data);
 * } catch (error) {
 *   const titleError = getFieldError(error as AxiosError, 'title');
 *   if (titleError) {
 *     setError('title', { type: 'manual', message: titleError });
 *   }
 *   }
 * }
 * ```
 */
export function getFieldError(
  error: AxiosError,
  fieldName: string
): string | undefined {
  const parsed = parseApiError(error);
  return parsed.details?.find((d: { field: string; message: string }) => d.field === fieldName)?.message;
}

/**
 * Получает все ошибки валидации в виде объекта { field: message }
 * Полезно для установки всех ошибок формы за раз
 *
 * @param error - Ошибка от axios
 * @returns Объект с ошибками по полям
 *
 * @example
 * ```ts
 * try {
 *   await api.createEvent(data);
 * } catch (error) {
 *   const validationErrors = getValidationErrors(error as AxiosError);
 *   // { title: 'Title is required', date: 'Date must be in the future' }
 *   setErrors(validationErrors);
 * }
 * ```
 */
export function getValidationErrors(
  error: AxiosError
): Record<string, string> {
  const parsed = parseApiError(error);
  const errors: Record<string, string> = {};

  if (parsed.details) {
    parsed.details.forEach((detail: { field: string; message: string }) => {
      errors[detail.field] = detail.message;
    });
  }

  return errors;
}

/**
 * Проверяет, является ли ошибка ошибкой валидации
 *
 * @param error - Ошибка от axios
 * @returns true если это ошибка валидации (400 с details)
 */
export function isValidationError(error: AxiosError): boolean {
  return (
    error.response?.status === 400 &&
    parseApiError(error).details !== undefined
  );
}

/**
 * Проверяет, является ли ошибка ошибкой авторизации
 *
 * @param error - Ошибка от axios
 * @returns true если это ошибка авторизации (401)
 */
export function isAuthError(error: AxiosError): boolean {
  return error.response?.status === 401;
}

/**
 * Проверяет, является ли ошибка ошибкой доступа
 *
 * @param error - Ошибка от axios
 * @returns true если это ошибка доступа (403)
 */
export function isForbiddenError(error: AxiosError): boolean {
  return error.response?.status === 403;
}

/**
 * Проверяет, является ли ошибка rate limiting
 *
 * @param error - Ошибка от axios
 * @returns true если это rate limiting (429)
 */
export function isRateLimitError(error: AxiosError): boolean {
  return error.response?.status === 429;
}

